/**
 * srLevels.ts (R137) — Support / Resistance level computation for the "S/R"
 * chart indicator. The S/R indicator is a CONTAINER for multiple source
 * methods; every method emits the same `SrLevel` shape so the renderer can
 * draw them all together regardless of how each was derived. Add a future
 * method by writing another `compute*Sr()` that returns `SrLevel[]`.
 *
 * First method — "HLDiff SR":
 *   Reuses the HLDiff histogram math (EMA of the Highs and EMA of the Lows of
 *   a chosen candle type; the bar value is the High−Low EMA spread, coloured
 *   by the slope of the two EMAs vs the previous bar). A level is created at a
 *   LOCAL PEAK of that spread — the bar where it stops rising and turns down —
 *   when the peak bar is a "dark" bar:
 *     • dark-green peak (both EMAs rising) → Resistance at the peak bar's HIGH
 *     • dark-red   peak (both EMAs falling) → Support   at the peak bar's LOW
 *   Light bars (only one EMA rising) at a peak produce nothing. The price is
 *   the RAW candle high/low (a real, tradeable level), even though the bar
 *   colouring comes from the transformed-candle EMAs.
 *
 * Active/inactive: the newest level in a stream is "active"; forming a level
 * deactivates the prior active one (Resistance ↔ Support alternate), so only
 * the most-recent level per stream is active. Older levels are kept (capped)
 * as faint inactive lines.
 *
 * Repaint note: levels track the live histogram — the latest peak is confirmed
 * once a later bar prints lower, exactly as the user sees the histogram turn.
 */
import { transformCandles, ema as emaOf } from './candleTransforms';

export interface SrCandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
}

export type SrSide = 'R' | 'S';
export type SrStream = 'CTF' | 'HTF';

export interface SrLevel {
  price: number;
  side: SrSide;
  /** True for the single most-recent level of the stream. */
  active: boolean;
  /** Interval (minutes) of the stream this level came from — for the label. */
  tfMinutes: number;
  /** Short source-method name shown on the level label, e.g. "HLDiff". */
  methodLabel: string;
  /** Bar time (UNIX seconds, IST-shifted) where the level formed. */
  formedAt: number;
  stream: SrStream;
}

export interface HlDiffSrConfig {
  /** Candle type for the High/Low EMAs (must match Chart Settings → HLDiff). */
  candleType: string;
  /** EMA period applied to both the Highs and the Lows. */
  emaPeriod: number;
}

/**
 * Compute HLDiff-SR levels for ONE candle stream — ALL levels in chronological
 * order (the caller caps for "levels" mode; "band" mode needs the full
 * history). The last level is flagged active.
 * @param candles  RAW candles for the stream (chart-TF or HTF).
 * @param cfg      candle type + EMA period.
 * @param tfMinutes interval of this stream (for labels).
 * @param stream   'CTF' | 'HTF'.
 */
export function computeHlDiffSr(
  candles: SrCandle[],
  cfg: HlDiffSrConfig,
  tfMinutes: number,
  stream: SrStream,
): SrLevel[] {
  const period = Math.max(1, Math.min(500, Math.floor(cfg.emaPeriod || 21)));
  if (!candles || candles.length < period + 4) return [];

  // Transform for the EMA inputs (Regular = raw); transforms preserve length
  // and bar order, so index i maps 1:1 onto the RAW candle used for the price.
  const tc = cfg.candleType === 'Regular'
    ? candles
    : (transformCandles(candles as any, cfg.candleType as any) as unknown as SrCandle[]);
  if (tc.length !== candles.length) return [];

  const highsE = emaOf(tc.map(c => c.high), period);
  const lowsE = emaOf(tc.map(c => c.low), period);

  // Spread (histogram value) + slope category per bar, matching the HLDiff
  // histogram exactly (loop starts one bar after warm-up so the slope vs the
  // previous bar is defined).
  const spread: Array<number | null> = new Array(candles.length).fill(null);
  // 1 = dark green (both rising), -1 = dark red (both falling), 0 = mixed/light.
  const cat: Array<number | null> = new Array(candles.length).fill(null);
  for (let i = period + 1; i < tc.length; i++) {
    const h = highsE[i], l = lowsE[i], ph = highsE[i - 1], pl = lowsE[i - 1];
    if (h == null || l == null || ph == null || pl == null) continue;
    if (!Number.isFinite(h) || !Number.isFinite(l) || !Number.isFinite(ph) || !Number.isFinite(pl)) continue;
    spread[i] = h - l;
    const hiUp = h > ph, loUp = l > pl;
    cat[i] = hiUp && loUp ? 1 : (!hiUp && !loUp ? -1 : 0);
  }

  // Walk the spread series; at each strict local peak (rose into p, falls
  // after) check the peak bar's category and emit a level at the RAW high/low.
  const levels: SrLevel[] = [];
  for (let i = period + 3; i < candles.length; i++) {
    const s = spread[i], s1 = spread[i - 1], s2 = spread[i - 2];
    if (s == null || s1 == null || s2 == null) continue;
    const roseIntoPeak = s1 > s2;     // rising into bar i-1
    const fallsAfterPeak = s < s1;    // bar i "starts falling"
    if (!roseIntoPeak || !fallsAfterPeak) continue;
    const p = i - 1;                  // the peak bar = "previous bar"
    const c = cat[p];
    // NON-REPAINTING / NO LOOK-AHEAD: the turn-down is only KNOWN once the
    // confirming bar `i` has closed (we need spread[i] < spread[i-1], which
    // requires bar i complete). So the level becomes available at bar i's
    // close — i.e. the next bar's open. We stamp `formedAt` there (not at the
    // peak bar's open), so on a lower-timeframe chart an HTF level appears only
    // after the HTF data that produced it was actually available. The price is
    // still the historical peak bar's high/low.
    const intervalSec = tfMinutes > 0 ? tfMinutes * 60 : Math.max(1, candles[i].time - candles[i - 1].time);
    const knownAt = (i + 1 < candles.length) ? candles[i + 1].time : candles[i].time + intervalSec;
    if (c === 1) {
      levels.push({ price: candles[p].high, side: 'R', active: false, tfMinutes, methodLabel: 'HLDiff', formedAt: knownAt, stream });
    } else if (c === -1) {
      levels.push({ price: candles[p].low, side: 'S', active: false, tfMinutes, methodLabel: 'HLDiff', formedAt: knownAt, stream });
    }
  }

  if (!levels.length) return [];
  // Newest level is the single active one (forming any level deactivates the
  // previously active opposite/older level).
  levels[levels.length - 1].active = true;
  return levels;
}

export interface RunPoint { time: number; value: number; }

/**
 * Build the "running band" for a stream: at each chart bar, the most-recent
 * Resistance and most-recent Support formed up to that bar. Produces two
 * stepped lines (running R / running S) plus the band points between them —
 * the evolving S/R channel. Sampled on the chart's own bar times so every
 * point places cleanly (works for the HTF stream too, whose level times are a
 * subset of the chart axis).
 */
export function buildRunningBand(
  levels: SrLevel[],
  barTimes: number[],
): { rPts: RunPoint[]; sPts: RunPoint[]; bandPts: { time: number; upper: number; lower: number }[] } {
  const rLv = levels.filter(l => l.side === 'R').sort((a, b) => a.formedAt - b.formedAt);
  const sLv = levels.filter(l => l.side === 'S').sort((a, b) => a.formedAt - b.formedAt);
  const times = [...barTimes].sort((a, b) => a - b);
  const rPts: RunPoint[] = [];
  const sPts: RunPoint[] = [];
  const bandPts: { time: number; upper: number; lower: number }[] = [];
  let ri = 0, si = 0;
  let curR: number | null = null, curS: number | null = null;
  for (const t of times) {
    while (ri < rLv.length && rLv[ri].formedAt <= t) { curR = rLv[ri].price; ri++; }
    while (si < sLv.length && sLv[si].formedAt <= t) { curS = sLv[si].price; si++; }
    if (curR != null) rPts.push({ time: t, value: curR });
    if (curS != null) sPts.push({ time: t, value: curS });
    if (curR != null && curS != null) bandPts.push({ time: t, upper: curR, lower: curS });
  }
  return { rPts, sPts, bandPts };
}

/* ─────────────────────────────────────────────────────────────────────────
 * Break / Respect events (R140) — visualisation of the S/R working/breached
 * logic. Uses the CLH candle body (its close + colour/direction) on the chart
 * timeframe against the running HTF Resistance / Support, with a two-stage
 * state machine so head-fakes are caught:
 *
 *   • Provisional breach: CLH body closes beyond R+buf (up) / S−buf (down) in
 *     the trend colour — armed, no event yet.
 *   • Confirmed breach: holds for `holds` qualifying closes within `window`
 *     bars without reclaiming → continuation (CONF_UP / CONF_DOWN).
 *   • False breach (trap): reclaims (closes back inside) or the CLH flips
 *     colour before confirmation → reversal (FALSE_UP / FALSE_DOWN).
 *   • Respected (no penetration): CLH tests the level then flips colour in
 *     front of it → mean-reversion (REJECT at R / BOUNCE at S).
 *
 * Evaluated on CLOSED bars only and against look-ahead-safe HTF levels, so the
 * events are non-repainting (a provisional breach emits nothing until it
 * resolves). buffer = max(bufFrac·(R−S), 0.05% of price).
 * ───────────────────────────────────────────────────────────────────────── */
export type SrEventKind = 'CONF_UP' | 'CONF_DOWN' | 'FALSE_UP' | 'FALSE_DOWN' | 'REJECT' | 'BOUNCE';
export interface SrEvent { time: number; kind: SrEventKind; price: number; }

export interface SrMarkerConfig {
  /** Buffer as a fraction of the (R − S) channel width. */
  bufFrac: number;
  /** `m` — bars allowed to resolve a provisional breach. */
  window: number;
  /** `n` — qualifying closes beyond the level to confirm a breach. */
  holds: number;
  /** `cd` — bars to suppress re-arming / respect after a false breach. */
  cooldown: number;
  /** `k` — a "test" of the level must be within this many bars for a respect. */
  testLookback: number;
  showConfirmed: boolean;
  showFalse: boolean;
  showRespected: boolean;
}

export function computeSrBreakRespectEvents(
  candles: SrCandle[],          // chart-TF CLOSED candles (raw OHLC)
  htfCandles: SrCandle[],       // HTF raw candles
  hlDiffCfg: HlDiffSrConfig,    // candle type + EMA period for the S/R levels
  htfMinutes: number,
  cfg: SrMarkerConfig,
): SrEvent[] {
  if (!candles || candles.length < 10 || !htfCandles || !htfCandles.length) return [];
  const htfLevels = computeHlDiffSr(htfCandles, hlDiffCfg, htfMinutes, 'HTF');
  if (!htfLevels.length) return [];

  // Running HTF R / S aligned to each chart bar (most-recent confirmed level).
  const n = candles.length;
  const rArr: (number | null)[] = new Array(n).fill(null);
  const sArr: (number | null)[] = new Array(n).fill(null);
  {
    const rLv = htfLevels.filter(l => l.side === 'R').sort((a, b) => a.formedAt - b.formedAt);
    const sLv = htfLevels.filter(l => l.side === 'S').sort((a, b) => a.formedAt - b.formedAt);
    let ri = 0, si = 0; let curR: number | null = null, curS: number | null = null;
    for (let i = 0; i < n; i++) {
      const t = candles[i].time;
      while (ri < rLv.length && rLv[ri].formedAt <= t) { curR = rLv[ri].price; ri++; }
      while (si < sLv.length && sLv[si].formedAt <= t) { curS = sLv[si].price; si++; }
      rArr[i] = curR; sArr[i] = curS;
    }
  }

  // CLH body close + direction (green = body rising) on the chart TF.
  const clh = transformCandles(candles as any, 'CLH') as unknown as SrCandle[];
  if (clh.length !== n) return [];
  const c = clh.map(b => b.close);
  const dir = clh.map(b => (b.close >= b.open ? 1 : -1));

  const bufFrac = Math.max(0.01, Math.min(1, cfg.bufFrac || 0.12));
  const m = Math.max(1, Math.min(50, Math.floor(cfg.window || 3)));
  const need = Math.max(1, Math.min(20, Math.floor(cfg.holds || 2)));
  const cd = Math.max(0, Math.min(50, Math.floor(cfg.cooldown || 2)));
  const k = Math.max(1, Math.min(50, Math.floor(cfg.testLookback || 3)));

  const events: SrEvent[] = [];
  let upArmed = false, upStart = -1, upHolds = 0, upCool = -1;
  let dnArmed = false, dnStart = -1, dnHolds = 0, dnCool = -1;
  let lastTestR = -1e9, lastTestS = -1e9;

  for (let i = 1; i < n; i++) {
    const R = rArr[i], S = sArr[i];
    if (R == null || S == null || !(R > S)) { upArmed = false; dnArmed = false; continue; }
    const buf = Math.max((R - S) * bufFrac, Math.abs(R) * 0.0005);
    const ci = c[i], cp = c[i - 1], di = dir[i], dp = dir[i - 1];
    const flipDown = dp === 1 && di === -1;
    const flipUp = dp === -1 && di === 1;
    if (ci >= R - buf && ci <= R + buf) lastTestR = i;
    if (ci <= S + buf && ci >= S - buf) lastTestS = i;
    let upEvt = false, dnEvt = false;

    // ── Resistance / up-breach ──
    if (!upArmed) {
      if (i > upCool && ci > R + buf && di === 1 && cp <= R + buf) { upArmed = true; upStart = i; upHolds = 1; }
    } else {
      if (ci < R || (di === -1 && ci <= R + buf)) {              // reclaim / colour flip = trap
        if (cfg.showFalse) { events.push({ time: candles[i].time, kind: 'FALSE_UP', price: R }); upEvt = true; }
        upArmed = false; upCool = i + cd;
      } else if (ci > R && di === 1) {
        upHolds++;
        if (upHolds >= need) { if (cfg.showConfirmed) { events.push({ time: candles[i].time, kind: 'CONF_UP', price: R }); upEvt = true; } upArmed = false; }
      }
      if (upArmed && (i - upStart) >= m) upArmed = false;        // expired → neutral
    }

    // ── Support / down-breach ──
    if (!dnArmed) {
      if (i > dnCool && ci < S - buf && di === -1 && cp >= S - buf) { dnArmed = true; dnStart = i; dnHolds = 1; }
    } else {
      if (ci > S || (di === 1 && ci >= S - buf)) {
        if (cfg.showFalse) { events.push({ time: candles[i].time, kind: 'FALSE_DOWN', price: S }); dnEvt = true; }
        dnArmed = false; dnCool = i + cd;
      } else if (ci < S && di === -1) {
        dnHolds++;
        if (dnHolds >= need) { if (cfg.showConfirmed) { events.push({ time: candles[i].time, kind: 'CONF_DOWN', price: S }); dnEvt = true; } dnArmed = false; }
      }
      if (dnArmed && (i - dnStart) >= m) dnArmed = false;
    }

    // ── Respect (no penetration) ──
    if (cfg.showRespected) {
      if (!upArmed && !upEvt && i > upCool && flipDown && ci <= R && (i - lastTestR) <= k) {
        events.push({ time: candles[i].time, kind: 'REJECT', price: R });
      }
      if (!dnArmed && !dnEvt && i > dnCool && flipUp && ci >= S && (i - lastTestS) <= k) {
        events.push({ time: candles[i].time, kind: 'BOUNCE', price: S });
      }
    }
  }
  return events;
}
